import type { CSSProperties } from "react";
import { STATE_ORDER, type EngagementState } from "../../api/engagements";

interface StateMachineHeaderProps {
  current: EngagementState;
}

const STATE_LABELS: Record<EngagementState, string> = {
  draft: "DRAFT",
  pending_authorization: "PENDING AUTH",
  active: "ACTIVE",
  paused: "PAUSED",
  concluded: "CONCLUDED",
  kill_switch_locked: "KILL SWITCH",
  revoked: "REVOKED",
};

export function StateMachineHeader({ current }: StateMachineHeaderProps) {
  // Determine the "past" states (those before the current in STATE_ORDER)
  const currentIdx = STATE_ORDER.indexOf(current);
  const isTerminal = current === "kill_switch_locked" || current === "revoked";

  return (
    <div
      style={{
        display: "flex",
        gap: 4,
        alignItems: "center",
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        flexWrap: "wrap",
      }}
    >
      {STATE_ORDER.map((state, idx) => {
        const isCurrent = state === current;
        const isPast = !isTerminal && currentIdx > 0 && idx < currentIdx;

        const stateStyle: CSSProperties = isCurrent
          ? {
              background: "var(--accent-subtle)",
              color: "var(--success)",
              border: "1px solid rgba(34,197,94,0.35)",
              fontWeight: 700,
            }
          : isPast
            ? {
                background: "var(--bg-tertiary)",
                color: "var(--text-muted)",
                border: "1px solid var(--border)",
                textDecoration: "line-through",
                opacity: 0.65,
              }
            : {
                background: "var(--bg-tertiary)",
                color: "var(--text-muted)",
                border: "1px solid var(--border)",
              };

        return (
          <span key={state} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span
              style={{
                padding: "3px 8px",
                borderRadius: 4,
                ...stateStyle,
              }}
            >
              {STATE_LABELS[state]}
            </span>
            {idx < STATE_ORDER.length - 1 && (
              <span style={{ color: "var(--text-muted)" }}>→</span>
            )}
          </span>
        );
      })}

      {/* Kill-switch and revoked are side-branch states, shown separately */}
      {(current === "kill_switch_locked" || current === "revoked") && (
        <>
          <span style={{ color: "var(--text-muted)" }}>|</span>
          <span
            style={{
              padding: "3px 8px",
              borderRadius: 4,
              background: "rgba(239,68,68,0.12)",
              color: "var(--error)",
              border: "1px solid rgba(239,68,68,0.35)",
              fontWeight: 700,
            }}
          >
            {STATE_LABELS[current]}
          </span>
        </>
      )}
    </div>
  );
}
